/**
 * Statsig Vue integration (tutor-70k.5, tutor-70k.11, rl-lif9).
 *
 * Thin wrapper around `@statsig/js-client` exposing `logEvent`,
 * `checkGate`, `getExperiment`, plus a Vue composable surface. Also wires
 * `@statsig/web-analytics` autocapture so the Statsig Web Analytics
 * dashboard and Users explorer populate. There is no session replay: no
 * DOM-recording SDK is installed.
 *
 * No-op when `VITE_STATSIG_CLIENT_KEY` is unset, so dev / Vitest
 * stay silent. Production wires the key via Cloudflare Pages env.
 *
 * Privacy posture (tutor-70k.8 + tutor-70k.11):
 *
 * Two parallel event streams, governed differently:
 *
 * 1. **Manual events** (`logEvent`) — pass through `assertSafe`
 *    against the property allowlist in `shared/analytics/events.ts`.
 *    Deny-listed keys (`answer_text`, `question_text`, ...) raise.
 * 2. **Autocapture** (page views, clicks, form submits) — filtered at the
 *    SDK level by `eventFilterFunc` (see `autocaptureAllows`). The tutor
 *    surface (`/subjects/:id`), phone capture, the seat-claim page
 *    (`/claim`) and the funding-request page (`/fund`), whose URLs each
 *    carry a bearer token (§9.4, §9.5), and admin pages are dropped
 *    entirely.
 *
 * Identity:
 * - A signed-in user is the same internal `user_id` we use server-side.
 *   Never email, never IP, never custom attributes that could re-identify.
 *   The device's anonymous ID is never attached to an account: a choice
 *   made on a shared device belongs to the device, not to whoever signs in
 *   next.
 * - A visitor without an account gets a random `anonymousID`, stored in
 *   `localStorage` under `revisionlab.statsig.anonymous_id` and minted only
 *   once they have accepted analytics on this device.
 *
 * Consent (contract A25, PRD §9.1.1, DPIA P6):
 * - The client does not exist without consent: a live `product_analytics`
 *   row for an account, or an "accepted" choice on this device for a
 *   visitor without one. The consent store decides which applies and is
 *   the only caller of `setAnalyticsConsent`.
 * - The gate is deliberately the client's LIFECYCLE, not a check at each
 *   log site: `logEvent`, `checkGate` and autocapture all need a live
 *   `client`, so with no client there is nothing to forget. A per-call-site
 *   check is not a gate.
 * - The SDK's own device storage goes through `consentGatedStorage`, so the
 *   SDK cannot put an identifier back on the device while consent is off.
 *
 * Analytics MUST NOT crash the SPA — every entrypoint is wrapped in
 * try/catch.
 */

import {
  StableID,
  StatsigClient,
  StatsigSession,
  type StatsigUser,
  type StorageProvider,
} from "@statsig/js-client";
import { AutoCapture } from "@statsig/web-analytics";

import type { EventProperties } from "@shared/analytics/events";
import { AnalyticsPayloadError, assertSafe } from "./payloadGuard";

const ANON_ID_STORAGE_KEY = "revisionlab.statsig.anonymous_id";

/**
 * Every `localStorage` key the Statsig SDK writes starts with this: its
 * stable ID, session ID, evaluation cache and queued events
 * (`@statsig/client-core`, 3.x).
 */
const SDK_STORAGE_PREFIX = "statsig.";

let client: StatsigClient | null = null;
// Who `client` was built for: an account's user id, or null for this
// device's anonymous visitor.
let clientUserId: string | null = null;

// A25 — flipped only by `setAnalyticsConsent`. Starts `false`, so the safe
// state is the state the app boots in: nobody has consented until the
// consent store has proved otherwise for the identity on screen.
let analyticsConsentGranted = false;

// Whether a client has been built in this page. The first construction is
// what installs `consentGatedStorage` as the SDK's global storage; before
// it, the SDK holds nothing in memory and still writes straight to
// `localStorage`, so it must not be touched at all.
let sdkStarted = false;

type QueuedEvent = {
  eventName: string;
  value?: string | number;
  metadata?: EventProperties;
};

// Manual events logged while the consent of `identity` is still being
// resolved: at boot, on sign-in, on an account switch. Views log on mount,
// which is before `/me/consents` answers, so without this every load-time
// event (`dashboard_viewed`) is lost. Memory only, never device storage;
// sent only if that same identity's consent comes back granted, and
// discarded by any refusal, withdrawal, failure or identity change.
const PENDING_LIMIT = 50;
let pending: { identity: string | null; events: QueuedEvent[] } | null = null;

function statsigKey(): string | undefined {
  const key = import.meta.env.VITE_STATSIG_CLIENT_KEY as string | undefined;
  return key || undefined;
}

function deviceStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Storage disabled (privacy mode, sandboxed frame).
    return null;
  }
}

function freshId(): string {
  return typeof crypto?.randomUUID === "function"
    ? crypto.randomUUID()
    : `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The SDK's storage provider, gated on consent.
 *
 * The SDK persists its stable ID, session ID and caches through one global
 * provider, and part of that runs from module-level visibility handlers
 * that outlive a shut-down client. Routed through here, nothing the SDK does
 * after a refusal, a withdrawal or a sign-out can write an identifier back
 * onto the device.
 */
const consentGatedStorage: StorageProvider = {
  isReady: () => true,
  isReadyResolver: () => null,
  getProviderName: () => "ConsentGatedLocalStorage",
  getItem: (key) =>
    analyticsConsentGranted ? (deviceStorage()?.getItem(key) ?? null) : null,
  setItem: (key, value) => {
    if (!analyticsConsentGranted) return;
    try {
      deviceStorage()?.setItem(key, value);
    } catch {
      // Quota exceeded: the SDK falls back to its in-memory copy.
    }
  },
  removeItem: (key) => {
    deviceStorage()?.removeItem(key);
  },
  getAllKeys: () => {
    const storage = deviceStorage();
    return storage ? Object.keys(storage) : [];
  },
};

function readOrMintAnonId(): string {
  const storage = deviceStorage();
  try {
    const existing = storage?.getItem(ANON_ID_STORAGE_KEY);
    if (existing) return existing;
    const fresh = freshId();
    storage?.setItem(ANON_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // Storage refused the write; an ephemeral id still lets this page's
    // events hang together.
    return `ephemeral-${Date.now()}`;
  }
}

function buildUser(userId: string | null): StatsigUser {
  if (userId) {
    return { userID: userId };
  }
  return { customIDs: { anonymousID: readOrMintAnonId() } };
}

/**
 * Whether an autocapture event from this page URL may leave the device.
 *
 * Exported so it can be exercised directly: it is the only thing standing
 * between a vendor and the URLs of every surface in the app, and driving
 * the real SDK to prove one path is blocked would prove it for whichever
 * paths the double happened to synthesise.
 *
 * A blocked prefix blocks the whole subtree rather than a leaf: these are
 * areas of the product, and a new child route under one of them must not
 * have to remember to opt out.
 */
export function autocaptureAllows(url: string): boolean {
  // Any /subjects/:id surface. SubjectView renders the AI progress
  // narrative, and ?session=1 opens the session overlay over the same URL
  // with tutor questions, mark schemes, typed answers and grades. All PII
  // per §6.8, so block the whole subtree from autocapture rather than a
  // single leaf (rl-eee.7). /session, /progress and /profile redirect
  // here, so the regex has to match the bare path.
  if (/\/subjects\/[^/]+(\?|#|$|\/)/.test(url)) return false;
  // Phone capture — uploads a photo of the student's handwritten
  // answer. The DOM previews the image; we don't want any
  // autocapture click/page_view events from this surface.
  if (/\/phone-capture(\?|#|$|\/)/.test(url)) return false;
  // §9.4 / rl-757.43 and §9.5 / rl-757.27 — THE SEAT-CLAIM AND
  // FUNDING-REQUEST TOKENS ARE IN THE PATH. Autocapture puts the literal
  // `page_url` in `event.metadata`, so a `page_view` from `/claim/{token}`
  // or `/fund/{token}` would hand a live bearer credential to a third party
  // in a field designed to be readable. That is not a Referer subtlety and
  // no header prevents it; only this filter does. Both views strip the
  // token from the URL on their first tick, which shortens the window but
  // does not close it — an autocapture `page_view` for the initial load is
  // exactly what fires inside it. Blocked on the prefix so the token-free
  // `/claim` and `/fund` are blocked too: the point is that no URL on
  // either surface is worth the risk of one that still carries a token.
  if (/\/(claim|fund)(\?|#|$|\/)/.test(url)) return false;
  // Admin pages — staff-only, no analytical value.
  if (/\/admin(\?|#|$|\/)/.test(url)) return false;
  return true;
}

/**
 * Bring the Statsig client up for `userId` (null: this device's anonymous
 * visitor), if and only if consent is live.
 *
 * Returns `false` — no client, no autocapture — when either the key is
 * unset (dev / Vitest) or consent has not been given. Callers may call
 * this freely at any point in boot; it is the consent flag, not the call
 * site, that decides whether anything happens.
 */
export async function configureStatsig(
  userId?: string | null,
): Promise<boolean> {
  const key = statsigKey();
  if (!key) return false;
  // A25 — the vendor client is never constructed without consent, so no
  // network call and no device identifier can precede it.
  if (!analyticsConsentGranted) return false;
  const identity = userId ?? null;
  // A client running for somebody else never carries over to this
  // identity, not even for the length of an `updateUser` round trip.
  if (client && clientUserId !== identity) void shutdownStatsig();
  if (client) return true;

  let created: StatsigClient | null = null;
  try {
    created = new StatsigClient(key, buildUser(identity), {
      environment: {
        tier:
          (import.meta.env.VITE_STATSIG_ENVIRONMENT as string | undefined) ??
          "production",
      },
      storageProvider: consentGatedStorage,
    });
    client = created;
    clientUserId = identity;
    sdkStarted = true;

    // Autocapture: page_view, click, form_submit, session_start. Built
    // directly rather than through `runStatsigAutoCapture`, which keeps one
    // instance per SDK key for the life of the page and would bind every
    // later client to the first one's listeners. Those listeners cannot be
    // removed, so the filter also drops anything once `created` is no
    // longer the live client: a refusal or a sign-out silences them.
    //
    // A click on a text field carries the field's current value — a typed
    // email address or password on /login — in `metadata.content`
    // (`@statsig/web-analytics` only spares checkboxes and buttons), so
    // clicks on `<input>` elements are dropped whole.
    const live = created;
    new AutoCapture(live, {
      eventFilterFunc: (event) =>
        client === live &&
        event.metadata?.tagName !== "input" &&
        autocaptureAllows(event.metadata?.page_url ?? ""),
    });

    await live.initializeAsync();
    return client === live;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[statsig] initialise failed:", err);
    if (client === created) await shutdownStatsig();
    return false;
  }
}

async function shutdownStatsig(): Promise<void> {
  const running = client;
  if (!running) return;
  // Detach first, synchronously: from this tick on `logEvent`, `checkGate`
  // and the autocapture filter all see no client, even while the flush
  // below is still in flight.
  client = null;
  clientUserId = null;
  try {
    await running.shutdown();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[statsig] shutdown failed:", err);
  }
}

/**
 * The single switch A25 hangs on: apply the consent the store resolved for
 * the identity on screen.
 *
 * Granting brings the client up for `userId` (idempotent for the same
 * identity). Refusing **tears it down inside the session** rather than
 * waiting for the next page load: the client is detached synchronously, so
 * nothing logged after this call reaches the vendor, and then flushed and
 * shut down.
 *
 * Only the consent store calls this.
 */
export async function setAnalyticsConsent(
  granted: boolean,
  userId?: string | null,
): Promise<void> {
  analyticsConsentGranted = granted;
  const queued = pending;
  pending = null;
  if (!granted) {
    await shutdownStatsig();
    return;
  }
  const identity = userId ?? null;
  const started = await configureStatsig(identity);
  if (!started || !client || queued?.identity !== identity) return;
  for (const event of queued.events) send(client, event);
}

/**
 * The identity on screen changed: stop the previous person's client and
 * hold this identity's events until `setAnalyticsConsent` resolves its
 * consent. Detaches and opens the queue synchronously, so nothing the next
 * view logs can reach the old client or be lost before the queue exists.
 *
 * Only the consent store calls this.
 */
export function beginAnalyticsConsent(userId: string | null): Promise<void> {
  analyticsConsentGranted = false;
  const stopped = shutdownStatsig();
  pending = { identity: userId, events: [] };
  return stopped;
}

/**
 * Remove every analytics identifier this app or the SDK has put on the
 * device: our anonymous ID and every `statsig.*` key.
 *
 * Called after `setAnalyticsConsent(false)` on a refusal, a withdrawal and
 * whenever the signed-in identity changes, so neither a withdrawn device
 * nor the next person on a shared one keeps an identifier from before.
 */
export function clearAnalyticsIdentifiers(): void {
  const key = statsigKey();
  if (key && sdkStarted) {
    try {
      // The SDK also holds its stable and session IDs in memory for the
      // life of the page. Re-seed both, so a later Accept in this tab
      // starts from new identifiers rather than reviving the old ones;
      // with consent off, the gated storage keeps the new ones off the
      // device.
      StableID.setOverride(freshId(), key);
      StatsigSession.overrideInitialSessionID(freshId(), key);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[statsig] identifier reset failed:", err);
    }
  }
  const storage = deviceStorage();
  if (!storage) return;
  try {
    for (const name of Object.keys(storage)) {
      if (name === ANON_ID_STORAGE_KEY || name.startsWith(SDK_STORAGE_PREFIX)) {
        storage.removeItem(name);
      }
    }
  } catch {
    // Storage refused; nothing more we can do from the page.
  }
}

/**
 * Whether the analytics client is live right now.
 *
 * Reports the vendor client's actual state, not the consent flag: that is
 * what a test asserting "no Statsig without consent" needs to see, and it
 * stays honest when the key is unset.
 */
export function isStatsigActive(): boolean {
  return client !== null;
}

export function logEvent(
  eventName: string,
  value?: string | number,
  metadata?: EventProperties,
): void {
  try {
    assertSafe(eventName, metadata as Record<string, unknown> | undefined);
  } catch (err) {
    if (err instanceof AnalyticsPayloadError) {
      // eslint-disable-next-line no-console
      console.warn("[statsig] payload rejected:", err.message);
      return;
    }
    throw err;
  }
  if (!client) {
    if (pending && pending.events.length < PENDING_LIMIT) {
      pending.events.push({ eventName, value, metadata });
    }
    return;
  }
  send(client, { eventName, value, metadata });
}

function send(target: StatsigClient, event: QueuedEvent): void {
  try {
    target.logEvent(
      event.eventName,
      event.value,
      event.metadata as Record<string, string> | undefined,
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[statsig] logEvent failed:", err);
  }
}

export function checkGate(gateName: string): boolean {
  if (!client) return false;
  try {
    return client.checkGate(gateName);
  } catch {
    return false;
  }
}

export function getExperiment(experimentName: string): Record<string, unknown> {
  if (!client) return {};
  try {
    const exp = client.getExperiment(experimentName);
    return (exp?.value ?? {}) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// --- Vue composables -------------------------------------------------------

export function useFeatureGate(gateName: string): boolean {
  return checkGate(gateName);
}

export function useExperiment(experimentName: string): Record<string, unknown> {
  return getExperiment(experimentName);
}

export function useStatsigEvent(): (
  eventName: string,
  value?: string | number,
  metadata?: EventProperties,
) => void {
  return logEvent;
}
